/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import React, { useCallback, useEffect, useRef, useState } from "react";

import { Element as SlateElement, Descendant, Range, Point, Editor, Transforms } from "slate";
import { HistoryEditor } from "slate-history";
import { Editable, ReactEditor, Slate } from "slate-react";
import styled from "styled-components";

import { MODIFIERS } from "src/components/Hotkeys/hotkeys";
import { SlashMenu } from "src/components/SlashMenu/SlashMenu";
import { SlashMenuOption } from "src/components/SlashMenu/slashMenuOptions";
import {
  EnhancedEditor,
  handleBackspace,
  handleEnter,
  handleShiftTab,
  handleTab,
  scrollSlateIntoView,
} from "src/components/ViennaEditor/lib/helpers";
import { handleSlashMenuKeydown, richTextHotkeys } from "src/components/ViennaEditor/lib/hotkeys";
import { linkAndEmailDecorator } from "src/components/ViennaEditor/lib/links";
import { CustomElement, CustomText } from "src/declare";
import { useOnMouseDownOutside } from "src/lib/hooks/useOnMouseDownOutside";

import { Element } from "./Element";
import { Leaf } from "./Leaf";

interface Props {
  editor: Editor;
  initialValue: Descendant[];
  readOnly: boolean; // whether the editor is read only
  textSubtype: string; // e.g. headerOne, quote, mono...
  handleChange?: () => void;
  slashMenu?: {
    options: SlashMenuOption[]; // list of options available in the slash menu
    onSelect: (option: SlashMenuOption) => void; // what to do when a slash menu option is selected
    positionRef: React.RefObject<HTMLDivElement>; // ref of an ancestor; needed to correctly position the slash menu
  };
  onPaste?: (event: React.ClipboardEvent<HTMLDivElement>) => void; // function to handle paste events
  onContextMenu?: (event: React.MouseEvent<HTMLDivElement>) => void; // function to handle context menu events
  autoFocus?: boolean; // focus the editor on mount
  editorId: string; // an id for the editor. used to identify the editor from other components to e.g. see if it is focused
  wrapperStyles?: React.CSSProperties; // styles to apply to the wrapper div
}
// SEE vienna-text-editor.md for more info on the editor
export function ViennaEditor({
  editor,
  initialValue,
  handleChange = () => {},
  readOnly,
  textSubtype,
  slashMenu,
  wrapperStyles,
  autoFocus,
  editorId,
  onPaste,
  onContextMenu,
}: Props) {
  const wrapperRef = useRef(null);

  const [displayedOptions, setDisplayedSlashMenuOptions] = useState<SlashMenuOption[] | null>(null); // a list of filtered slashMenuOptions
  const [commandRange, setCommandRange] = useState<Range | null>(null); // a slate Range object that marks the slash command
  const [userSelect, setUserSelect] = useState<"text" | "none">("none"); // whether the user can select text. used only as a wekbkit value to prevent safari selection apocalypse

  const [selectedIndex, setSelectedIndex] = useState(0); // the index of the selected slash menu option
  const [inputType, setInputType] = useState<"keyboard" | "mouse">("keyboard"); // whether the user is using the mouse or keyboard to navigate the slash menu
  const slashMenuOpen = displayedOptions && commandRange;
  const closeSelf = () => {
    // close the slash menu
    setCommandRange(null);
    setDisplayedSlashMenuOptions(null);
  };
  const handleSelect = () => {
    Transforms.delete(editor, { at: commandRange! }); // delete the range containing the command
    closeSelf(); // close self
    slashMenu?.onSelect(displayedOptions![selectedIndex]); // select the option
    EnhancedEditor.resetFonts(editor); // reset fonts
  };
  /**
   * Runs on keypress; used to bind keys to commands.
   */
  const handleKeydown = (e: React.KeyboardEvent) => {
    // check for formatting commands
    richTextHotkeys(editor, e);
    if (slashMenuOpen) {
      const { handled } = handleSlashMenuKeydown({
        e,
        inputType,
        setInputType,
        selectedIndex,
        setSelectedIndex,
        displayedOptions,
        selectOption: handleSelect,
        close: closeSelf,
      });
      if (handled) {
        e.stopPropagation();
        return;
      }
    }
    let handled = true;
    switch (e.key) {
      case "z":
        // check for undo/redo hotkeys
        if (
          (MODIFIERS.COMMAND === "meta" ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey) &&
          !e.shiftKey &&
          !e.altKey &&
          (editor as unknown as HistoryEditor).history.undos.length > 0
        ) {
          HistoryEditor.undo(editor as unknown as HistoryEditor);
          e.preventDefault();
          e.stopPropagation();
          break;
        }
        if (
          (MODIFIERS.COMMAND === "meta" ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey) &&
          !e.altKey &&
          e.shiftKey &&
          (editor as unknown as HistoryEditor).history.redos.length > 0
        ) {
          HistoryEditor.redo(editor as unknown as HistoryEditor);
          e.preventDefault();
          e.stopPropagation();
          break;
        }
        break;
      case "a":
        if (MODIFIERS.COMMAND === "meta" ? e.metaKey : e.ctrlKey) {
          // select all
          Transforms.select(editor, []);
          e.preventDefault();
        }
        break;
      case "Tab":
        if (editor.selection && !e.shiftKey) {
          try {
            handleTab(editor, e);
          } catch {
            // Needed if the user is spamming the tab key and the editor is not ready to handle it.
            e.preventDefault();
          }
        } else if (e.shiftKey) {
          handleShiftTab(editor, e);
        }
        break;
      case "Enter":
        handleEnter(editor, e);
        break;
      case "Backspace":
        if (!e.metaKey && !e.ctrlKey) {
          handleBackspace(editor, e);
        } else {
          Transforms.delete(editor, { unit: "line", reverse: true });
          e.preventDefault();
        }
        break;
      case "Escape":
        removeFocus();
        break;
      default:
        handled = false;
        break;
    }
    if (handled) {
      e.stopPropagation();
    }
  };

  /**
   * Runs anytime the editor is updated. Used for slash menu management.
   */
  const handleEditorChange = (): void => {
    // call the passed in handleChange function
    handleChange();
    const shouldMergeLists: any = editor.operations.find(
      (op) => op.type.slice(op.type.length - 4, op.type.length) === "node"
    ); // check if we should do a check for merging lists
    if (shouldMergeLists && shouldMergeLists.path) {
      // if we fail to merge the lists in a range, just merge the entire editor
      try {
        EnhancedEditor.mergeLists(editor, Editor.range(editor, shouldMergeLists.path));
      } catch {
        // if merging fails, ignore it
      }
    }
    const { selection } = editor;

    // checking for bulleted or numbered lists
    if (editor.operations[0]?.type === "insert_text" && selection) {
      const currentNode = Editor.node(editor, selection)[0] as CustomText;

      const currentNodeText = currentNode && currentNode.text;
      if (currentNodeText === "- ") {
        // if the user has typed "- ", we might want to create a bullet if we are not already in one
        const currentElementType = (editor.getFragment()[0] as CustomElement)?.type;
        if (currentElementType === "plain") {
          // if the current fragment type is plain, create a bullet list
          EnhancedEditor.bulletedList(editor);
          // merge lists if necessary
          EnhancedEditor.mergeLists(editor);
        }
      } else if (currentNodeText?.length > 2) {
        const fragment: any = editor.getFragment()[0];
        // if the user has typed a number, followed by ". ", we might want to create a numbered list
        const numberRegex = currentNodeText.match(/[0123456789]+\. /);

        if (numberRegex && currentNodeText === numberRegex[0] && fragment.type === "plain") {
          // if the current fragment type is plain, create a numbered list and contains nothing but the number, create a numbered list
          EnhancedEditor.numberedList(editor, Number(currentNodeText.slice(0, currentNodeText.length - 2)));
          // merge lists if necessary
          EnhancedEditor.mergeLists(editor);
        }
      }
    }

    // if slash menu is enabled, do slash menu stuff
    if (slashMenu) {
      const isContentChange = editor.operations.some((op) => op.type !== "set_selection"); // check if the change is a content change
      if (!isContentChange) {
        // if it's not a content change, we do not want to open the slash menu. this prevents the slash menu from opening when the user moves the cursor around a slash
        setDisplayedSlashMenuOptions(null); // if there are no commands, set the slashCommands to null
        setCommandRange(null); // if there are no commands, set the commandRange to null
        return;
      }
      // sanity check: make sure selection exists & we never want to show slash menu when there is an expanded selection
      if (selection && Range.isCollapsed(selection)) {
        const [start] = Range.edges(selection); // get the start Point of the selection (though this is the same as the end Point in a collapsed selection)
        // get the starting point of the word second before the selection (due to commands such as heading 1 where there is a space)
        const prevWordStartPoint: Point | undefined = Editor.before(editor, start, { unit: "word", distance: 2 });
        const beforePoint =
          prevWordStartPoint && (Editor.before(editor, prevWordStartPoint) || prevWordStartPoint); // get the point before the start of the previous word if it exists. needed to include the potential '/' preceding the word
        const prevWordRange = beforePoint && Editor.range(editor, beforePoint, start); // get the range from start of prevWord to selection
        const prevWordText = prevWordRange && Editor.string(editor, prevWordRange); // grab the text

        const prevWordSlashMatches = prevWordText && prevWordText.match(/\/(.*)/); // check the previous word for slashes

        if (prevWordSlashMatches) {
          // create a point for the final slash in the prevWordText by adjusting the offset of the prevWordRange
          const slashPoint = {
            path: start.path,
            offset: Math.max(prevWordRange.focus.offset - prevWordText.length + prevWordText.lastIndexOf("/"), 0),
          };
          // create a range from the final slash to the end of the selection
          const slashRange = Editor.range(editor, slashPoint, start);
          // set the slashMenuOptions to the list of potential slash menu options
          setDisplayedSlashMenuOptions(
            EnhancedEditor.getFilteredSlashMenuOptions(slashMenu.options, slashRange, editor)
          );
          // set the commandRange to the slashRange
          setCommandRange(slashRange);
          // break out of the function
          return;
        }
      }
      setDisplayedSlashMenuOptions(null); // if there are no commands, set the slashCommands to null
      setCommandRange(null); // if there are no commands, set the commandRange to null
    }
    return;
  };

  // A leaf rendering function which we can use to customize the styling of leaves (pieces of text with the same styling applied)
  const renderLeaf = useCallback((props: any) => <Leaf {...props} />, []);
  const renderElement = useCallback(
    (props: any) => <Element {...props} subType={textSubtype} />,
    [editor.children]
  );
  const renderPlaceholder = ({ children, attributes }: any) => {
    if (!readOnly && (editor.children[0] as SlateElement).type === "plain") {
      return <StyledPlaceholder {...attributes}>{children}</StyledPlaceholder>;
    }
    return <></>;
  };

  // useEffect to autofocus when needed. slate is a bitch in this area, tread carefully. we were running into an issue
  // where only calling ReactEditor.focus would not actually focus the editor, but Editor.isFocused would be true - causing
  // various hotkey failures. ask jack for more info
  useEffect(() => {
    if (autoFocus) {
      // focus editor
      ReactEditor.focus(editor);
      // to magic to make stupid thing actually focus
      Transforms.select(editor, { anchor: Editor.start(editor, []), focus: Editor.start(editor, []) });
    }
  }, []);

  // would be great if we could find a way to do this without refs. in the slate examples, they get focus management on mouseDownOutside for
  // free. we must be doing something to get in the way of that
  const removeFocus = () => {
    // Transforms.deselect(editor);
    ReactEditor.deselect(editor);
    ReactEditor.blur(editor);
  };
  useOnMouseDownOutside(wrapperRef, () => {
    removeFocus(); // need to do this for chrome
    setUserSelect("none"); // and this for safari
  });

  const refocusOnEditor = () => ReactEditor.focus(editor);

  return (
    <div
      ref={wrapperRef}
      style={wrapperStyles}
      onContextMenu={(e) => e.stopPropagation()}
      onMouseDown={() => {
        setUserSelect("text");
      }}
    >
      <Slate editor={editor} value={initialValue} onChange={handleEditorChange}>
        {/* todo: id should include blockId: `slate-${blockId}` */}
        <Editable
          id={editorId}
          onKeyDown={handleKeydown}
          renderLeaf={renderLeaf}
          renderElement={renderElement}
          style={{
            height: "100%",
            width: "100%",
            padding: "15px",
            position: "relative",
            WebkitUserSelect: userSelect,
          }}
          placeholder='Type "/" for commands'
          renderPlaceholder={renderPlaceholder}
          readOnly={readOnly}
          decorate={linkAndEmailDecorator}
          onPaste={(e) => onPaste && onPaste(e)}
          onContextMenu={(e) => onContextMenu && onContextMenu(e)}
          scrollSelectionIntoView={scrollSlateIntoView}
        />
        {slashMenuOpen && (
          <SlashMenu
            close={closeSelf}
            handleSelect={handleSelect}
            displayedOptions={displayedOptions}
            commandRange={commandRange}
            elementRef={slashMenu?.positionRef}
            inputType={inputType}
            setInputType={setInputType}
            selectedIndex={selectedIndex}
            setSelectedIndex={setSelectedIndex}
            refocusOnEditor={refocusOnEditor}
          />
        )}
      </Slate>
    </div>
  );
}

const StyledPlaceholder = styled.div`
  position: absolute;
  top: 15px;
  font-weight: normal;
  font-style: normal;
  padding-right: 15px;
  user-select: none;
  -webkit-user-select: none;
`;
